import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Radio, Clock, AlertCircle, Send, RefreshCw } from 'lucide-react';
import axios from 'axios';
import HlsPlayer from '../components/HlsPlayer';
import { useAuth, API, BACKEND_URL } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import PaymentProviderDialog from '../components/PaymentProviderDialog';

const LiveSeminarPage = () => {
  const { slug } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const chatBottomRef = useRef(null);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatType, setChatType] = useState('comment');
  const [chatLoading, setChatLoading] = useState(false);
  const [pageError, setPageError] = useState(null);
  const [paymentOpen, setPaymentOpen] = useState(false);

  const fetchSeminar = useCallback(async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/live-seminars/${slug}`, { headers });
      setData(res.data);
    } catch (e) {
      setPageError(e.response?.status === 404 ? 'not_found' : 'load_failed');
    } finally {
      setLoading(false);
    }
  }, [slug, token]);

  const fetchChat = useCallback(async () => {
    if (!token || !data?.live_seminar?.id || !data?.access_allowed) return;
    try {
      const res = await axios.get(
        `${API}/live-seminars/${data.live_seminar.id}/chat`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setChatMessages(res.data);
    } catch (_e) {
      // silent — do not interrupt the player
    }
  }, [token, data?.live_seminar?.id, data?.access_allowed]);

  useEffect(() => { fetchSeminar(); }, [fetchSeminar]);

  useEffect(() => {
    const ls = data?.live_seminar;
    if (!data?.access_allowed || ls?.status !== 'live' || !ls?.chat_enabled) return;
    fetchChat();
    const id = setInterval(fetchChat, 8000);
    return () => clearInterval(id);
  }, [data, fetchChat]);

  // HLS handled by HlsPlayer component rendered in the JSX below

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || !data?.live_seminar?.id) return;
    setChatLoading(true);
    try {
      await axios.post(
        `${API}/live-seminars/${data.live_seminar.id}/chat`,
        { message: chatInput.trim(), type: chatType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setChatInput('');
      await fetchChat();
    } catch (_e) {
      // silent
    } finally {
      setChatLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-misel-bg flex items-center justify-center">
        <div className="animate-pulse text-white text-sm">Loading…</div>
      </div>
    );
  }

  if (pageError === 'not_found') {
    return (
      <div className="min-h-screen bg-misel-bg flex flex-col items-center justify-center text-center px-4">
        <h1 className="text-2xl font-bold text-white mb-3">Seminar not found</h1>
        <Link to="/" className="text-primary text-sm hover:underline">Back to home</Link>
      </div>
    );
  }

  if (pageError === 'load_failed') {
    return (
      <div className="min-h-screen bg-misel-bg flex flex-col items-center justify-center text-center px-4">
        <AlertCircle size={40} className="text-red-400 mb-4" />
        <h1 className="text-xl font-bold text-white mb-3">Failed to load seminar</h1>
        <button
          onClick={() => { setPageError(null); setLoading(true); fetchSeminar(); }}
          className="text-primary text-sm hover:underline"
        >
          Try again
        </button>
      </div>
    );
  }

  const { live_seminar, access_allowed, reason, player } = data;
  const isLive = player?.mode === 'live';
  const isReplay = player?.mode === 'replay';
  const isReplayLinked = player?.mode === 'replay_linked';
  const hasVideoUrl = isLive ? !!player.hls_url : isReplay ? !!player.vod_hls_url : false;
  const ppv = live_seminar?.ppv_purchase;
  const ppvAmountLabel = ppv
    ? `${ppv.amount?.toFixed ? ppv.amount.toFixed(2) : ppv.amount} ${ppv.currency || 'EUR'}`
    : '';

  return (
    <div className="min-h-screen bg-misel-bg">
      <Navbar />
      <div className="pt-20 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-6"
          >
            <div className="flex items-center gap-3 mb-2">
              {live_seminar.status === 'live' && (
                <span className="flex items-center gap-1.5 text-xs font-bold text-red-400 uppercase tracking-widest">
                  <Radio size={11} className="animate-pulse" />
                  LIVE
                </span>
              )}
              {live_seminar.status === 'published' && (
                <span className="text-xs text-primary uppercase tracking-widest font-semibold">
                  REPLAY
                </span>
              )}
              {live_seminar.status === 'scheduled' && (
                <span className="flex items-center gap-1.5 text-xs text-gray-400 uppercase tracking-widest">
                  <Clock size={11} />
                  UPCOMING
                </span>
              )}
            </div>
            <h1 className="font-roboto font-bold text-2xl md:text-3xl text-white leading-tight break-words">
              {live_seminar.title}
            </h1>
            {live_seminar.description && (
              <p className="mt-2 text-gray-400 text-sm leading-relaxed max-w-2xl whitespace-pre-line break-words">
                {live_seminar.description}
              </p>
            )}
          </motion.div>

          {/* Not logged in */}
          {!access_allowed && reason === 'login_required' && (
            <div className="flex flex-col items-center justify-center min-h-64 bg-misel-card border border-white/10 rounded-sm p-8 text-center">
              <Lock size={36} className="text-gray-600 mb-4" />
              <p className="text-white font-semibold mb-1">Sign in to access this live seminar</p>
              <p className="text-gray-500 text-sm mb-5">You need an account to watch live seminars.</p>
              <div className="flex gap-3">
                <Button onClick={() => navigate('/login')}>Sign In</Button>
                <Button variant="outline" onClick={() => navigate('/register')}
                  className="border-white/20 text-white hover:bg-white/10">
                  Register
                </Button>
              </div>
            </div>
          )}

          {/* Logged in but not entitled */}
          {!access_allowed && reason === 'no_entitlement' && ppv && (
            <div className="flex flex-col items-center justify-center min-h-64 bg-misel-card border border-white/10 rounded-sm p-8 text-center">
              <Lock size={36} className="text-gray-600 mb-4" />
              <p className="text-white font-semibold mb-1">Buy live access</p>
              <p className="text-gray-500 text-sm mb-2">
                Watch this live seminar with a one-time purchase.
              </p>
              <p className="text-primary font-bold text-xl mb-2">{ppvAmountLabel}</p>
              {ppv.includes_replay && (
                <p className="text-gray-500 text-xs mb-5">Includes replay access when the replay is published.</p>
              )}
              <div className="flex flex-wrap gap-3 justify-center">
                <Button onClick={() => setPaymentOpen(true)}>Buy Live Access</Button>
                <Button variant="outline" onClick={() => navigate('/pricing')}
                  className="border-white/20 text-white hover:bg-white/10">
                  View Plans
                </Button>
              </div>
            </div>
          )}

          {!access_allowed && reason === 'no_entitlement' && !ppv && (
            <div className="flex flex-col items-center justify-center min-h-64 bg-misel-card border border-white/10 rounded-sm p-8 text-center">
              <Lock size={36} className="text-gray-600 mb-4" />
              <p className="text-white font-semibold mb-1">Subscription required</p>
              <p className="text-gray-500 text-sm mb-5">
                A plan subscription is required to access this seminar.
              </p>
              <Button onClick={() => navigate('/pricing')}>View Plans</Button>
            </div>
          )}

          {/* Entitled view */}
          {access_allowed && (
            <div className="flex flex-col lg:flex-row gap-6">

              {/* Player column */}
              <div className="flex-1 min-w-0">
                {hasVideoUrl ? (
                  <div className="aspect-video bg-black rounded-sm overflow-hidden">
                    <HlsPlayer
                      src={isLive ? player.hls_url : player.vod_hls_url}
                      backendUrl={BACKEND_URL}
                      videoRef={videoRef}
                      className="w-full h-full"
                      controls
                      autoPlay={isLive}
                      playsInline
                      onError={(err) => { console.error('HLS player error', err); }}
                    />
                  </div>
                ) : player?.mode === 'scheduled' ? (
                  <div className="aspect-video bg-misel-card border border-white/10 rounded-sm flex flex-col items-center justify-center text-center p-6">
                    <Clock size={40} className="text-primary mb-4" />
                    <p className="text-white font-bold text-lg mb-2">Seminar scheduled</p>
                    {live_seminar.scheduled_start_at && (
                      <p className="text-gray-400 text-sm">
                        {new Date(live_seminar.scheduled_start_at).toLocaleString()}
                      </p>
                    )}
                    <p className="text-gray-500 text-xs mt-3">Check back when the live starts.</p>
                  </div>
                ) : player?.mode === 'waiting_for_stream' || player?.mode === 'live' ? (
                  <div className="aspect-video bg-black rounded-sm flex items-center justify-center">
                    <p className="text-gray-400 text-sm">Live stream is being prepared.</p>
                  </div>
                ) : isReplayLinked ? (
                  <div className="aspect-video bg-misel-card border border-white/10 rounded-sm flex flex-col items-center justify-center text-center p-6">
                    <RefreshCw size={40} className="text-primary mb-4 animate-spin" />
                    <p className="text-white font-bold text-lg mb-1">Replay linked</p>
                    <p className="text-gray-400 text-sm">Replay linked, preparing player.</p>
                  </div>
                ) : player?.mode === 'processing' ? (
                  <div className="aspect-video bg-misel-card border border-white/10 rounded-sm flex flex-col items-center justify-center text-center p-6">
                    <RefreshCw size={40} className="text-primary mb-4 animate-spin" />
                    <p className="text-white font-bold text-lg mb-1">Replay processing</p>
                    <p className="text-gray-400 text-sm">The replay will be available shortly.</p>
                  </div>
                ) : (
                  <div className="aspect-video bg-misel-card border border-white/10 rounded-sm flex items-center justify-center">
                    <p className="text-gray-500 text-sm">Player not available yet.</p>
                  </div>
                )}
              </div>

              {/* Chat column */}
              {live_seminar.chat_enabled && (
                <div className="w-full lg:w-80 flex-shrink-0">
                  <div className="flex flex-col bg-misel-card border border-white/10 rounded-sm h-[420px] lg:h-full">
                    <div className="px-4 py-3 border-b border-white/10">
                      <h3 className="text-sm font-semibold text-white">
                        {live_seminar.questions_enabled ? 'Chat & Questions' : 'Live Chat'}
                      </h3>
                    </div>

                    <div className="flex-1 overflow-y-auto p-3 space-y-2">
                      {chatMessages.length === 0 && (
                        <p className="text-gray-500 text-xs text-center mt-6">No messages yet</p>
                      )}
                      {chatMessages.map((msg) => (
                        <div key={msg.id} className="text-sm">
                          <span className="text-primary font-medium text-xs">
                            {msg.user_name || 'Member'}
                          </span>
                          {msg.type === 'question' && (
                            <span className="ml-1 text-xs text-yellow-400 font-semibold">[?]</span>
                          )}
                          <span className="text-gray-300 ml-1 break-words">{msg.message}</span>
                        </div>
                      ))}
                      <div ref={chatBottomRef} />
                    </div>

                    {live_seminar.status === 'live' && (
                      <form
                        onSubmit={sendMessage}
                        className="border-t border-white/10 p-3 flex flex-col gap-2"
                      >
                        {live_seminar.questions_enabled && (
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => setChatType('comment')}
                              className={`text-xs px-2 py-1 rounded-sm border transition-colors ${
                                chatType === 'comment'
                                  ? 'bg-primary border-primary text-white'
                                  : 'border-white/20 text-gray-400 hover:border-white/40'
                              }`}
                            >
                              Comment
                            </button>
                            <button
                              type="button"
                              onClick={() => setChatType('question')}
                              className={`text-xs px-2 py-1 rounded-sm border transition-colors ${
                                chatType === 'question'
                                  ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-300'
                                  : 'border-white/20 text-gray-400 hover:border-white/40'
                              }`}
                            >
                              Question
                            </button>
                          </div>
                        )}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={chatInput}
                            onChange={(e) => setChatInput(e.target.value)}
                            placeholder="Type a message…"
                            maxLength={500}
                            className="flex-1 bg-white/5 border border-white/15 rounded-sm px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-primary min-w-0"
                          />
                          <button
                            type="submit"
                            disabled={chatLoading || !chatInput.trim()}
                            className="px-3 py-2 bg-primary text-white rounded-sm disabled:opacity-40 hover:bg-primary/90 transition-colors flex-shrink-0"
                          >
                            <Send size={14} />
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
      <PaymentProviderDialog
        open={paymentOpen}
        onOpenChange={setPaymentOpen}
        mode="live_seminar_ppv"
        liveSeminarId={live_seminar.id}
        amountLabel={ppv ? `Live seminar access: ${ppvAmountLabel}` : ''}
        basePrice={ppv?.amount || 0}
      />
    </div>
  );
};

export default LiveSeminarPage;
